import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { PageHeader } from '@/components/efs/PageHeader';
import { useFileLibraryFeatureEnabled } from '@/features/file-library/useFileLibraryFeature';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';
import type { FileLibrarySelection, FileSourceMode } from '@/features/file-library/types';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';

import { SourceFileDropzone } from './components/SourceFileDropzone';
import { SourceFileTable } from './components/SourceFileTable';
import { ImportSheetsSummary } from './components/ImportSheetsSummary';
import { ValidationMessageList } from './components/ValidationMessageList';
import { StagingRowDrawer } from './components/StagingRowDrawer';
import type { StagingRowDto } from './types';
import { PromoteAction } from './components/PromoteAction';

import {
  useAttachSourceFileFromLibrary,
  useSourceFiles,
  useUploadSourceFile,
} from './useSourceFiles';
import { useImportJob, useStartImportJob } from './useImportJob';
import { useStagingRows } from './useStagingRows';
import { sourceFileTypes, type ImportType, type SourceFileDto, type SourceFileType } from './types';

type StepId = 1 | 2 | 3 | 4 | 5;

const STEPS: { id: StepId; key: string }[] = [
  { id: 1, key: 'step1' },
  { id: 2, key: 'step2' },
  { id: 3, key: 'step3' },
  { id: 4, key: 'step4' },
  { id: 5, key: 'step5' },
];

const sourceTypeToImportType = (t: SourceFileType): ImportType | null => {
  if (t === 'operation_workbook') return 'operation_workbook';
  if (t === 'ups_estimate_workbook') return 'ups_estimate_workbook';
  if (t === 'price_reference_docx') return 'price_reference_docx';
  return null;
};

const Stepper = ({ current }: { current: StepId }) => {
  const { t } = useTranslation();
  return (
    <ol className="flex flex-wrap items-center gap-2 border-b border-efs-border pb-3">
      {STEPS.map((s, idx) => {
        const isActive = s.id === current;
        const isDone = s.id < current;
        return (
          <li key={s.id} className="flex items-center gap-2">
            <span
              className={cn(
                'inline-flex h-6 w-6 items-center justify-center rounded-full border text-xs font-semibold',
                isActive && 'border-efs-info bg-efs-info text-white',
                isDone && 'border-efs-success bg-efs-success text-white',
                !isActive && !isDone && 'border-efs-border text-efs-text-muted',
              )}
              aria-current={isActive ? 'step' : undefined}
            >
              {s.id}
            </span>
            <span
              className={cn(
                'text-sm',
                isActive ? 'font-semibold text-efs-text-primary' : 'text-efs-text-muted',
              )}
            >
              {t(`efs.imports.wizard.${s.key}.title`, { ns: 'imports' })}
            </span>
            {idx < STEPS.length - 1 ? (
              <span className="mx-1 h-px w-6 bg-efs-border" aria-hidden />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
};

export const ImportWizardPage = () => {
  const { t } = useTranslation();
  const { batchId } = useParams();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const fileLibraryEnabled = useFileLibraryFeatureEnabled();

  const [step, setStep] = useState<StepId>(1);
  const [sourceFileType, setSourceFileType] = useState<SourceFileType>('operation_workbook');
  const [fileSourceMode, setFileSourceMode] = useState<FileSourceMode>('local');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [selectedLibraryFile, setSelectedLibraryFile] = useState<FileLibrarySelection | null>(null);
  const [libraryAttachError, setLibraryAttachError] = useState<unknown>(null);
  const [uploadedFile, setUploadedFile] = useState<SourceFileDto | null>(null);
  const [importJobId, setImportJobId] = useState<string | null>(null);

  const sourceFilesQuery = useSourceFiles(batchId);
  const upload = useUploadSourceFile(batchId);
  const attachFromLibrary = useAttachSourceFileFromLibrary(batchId);
  const startJob = useStartImportJob();
  const job = useImportJob(importJobId ?? undefined);
  const sampleRows = useStagingRows(importJobId ?? undefined, { per_page: 5 });
  // Round-6 item 8: selecting a summary issue opens the affected staging row
  // in the same drawer the review page uses, with the editable control
  // focused (spec-13 §8.5).
  const [summaryRow, setSummaryRow] = useState<StagingRowDto | null>(null);

  const importType = useMemo(() => sourceTypeToImportType(sourceFileType), [sourceFileType]);
  const canUseFileLibrary =
    fileLibraryEnabled &&
    (hasPermission(user, 'file_library.read') || hasPermission(user, 'file_library.manage'));
  const effectiveFileSourceMode = canUseFileLibrary ? fileSourceMode : 'local';
  const summary = job.data?.summary;
  const status = job.data?.status;

  const canAdvance = (() => {
    if (step === 1) return !!importType;
    if (step === 2) {
      if (uploadedFile) return true;
      return effectiveFileSourceMode === 'library' ? !!selectedLibraryFile : !!pendingFile;
    }
    if (step === 3) return !!uploadedFile;
    if (step === 4) return status === 'ready_to_promote';
    return false;
  })();

  const handleSelectFile = (file: File) => {
    setPendingFile(file);
    setLibraryAttachError(null);
  };

  const handleUpload = async () => {
    try {
      setLibraryAttachError(null);
      const file =
        effectiveFileSourceMode === 'library'
          ? selectedLibraryFile
            ? await attachFromLibrary.mutateAsync({
                companyFileId: selectedLibraryFile.company_file_id,
                sourceFileType,
              })
            : null
          : pendingFile
            ? await upload.mutateAsync({ file: pendingFile, sourceFileType })
            : null;
      if (!file) return;
      setUploadedFile(file);
      setStep(3);
    } catch (e: unknown) {
      if (effectiveFileSourceMode === 'library') {
        setLibraryAttachError(e);
      }
    }
  };

  const handleStartParse = async () => {
    if (!uploadedFile || !importType) return;
    const j = await startJob.mutateAsync({
      fileId: uploadedFile.source_file_id,
      importType,
    });
    setImportJobId(j.import_job_id);
    setStep(4);
  };

  const handleViewReview = () => {
    if (importJobId && batchId) {
      navigate(`/batches/${batchId}/imports/${importJobId}`);
    }
  };

  const goNext = () => {
    if (step === 2) {
      void handleUpload();
      return;
    }
    if (step === 3) {
      void handleStartParse();
      return;
    }
    setStep((s) => (s < 5 ? ((s + 1) as StepId) : s));
  };

  const goBack = () => setStep((s) => (s > 1 ? ((s - 1) as StepId) : s));

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.imports.wizard.title', { ns: 'imports' })}
        subtitle={t('efs.imports.wizard.subtitle', { ns: 'imports' })}
      />

      <Stepper current={step} />

      {step === 1 ? (
        <section className="space-y-3 rounded-md border border-efs-border bg-efs-bg-panel p-4">
          <h2 className="text-sm font-semibold">
            {t('efs.imports.wizard.step1.title', { ns: 'imports' })}
          </h2>
          <p className="text-xs text-efs-text-muted">
            {t('efs.imports.wizard.step1.description', { ns: 'imports' })}
          </p>
          <Select
            value={sourceFileType}
            onValueChange={(v) => setSourceFileType(v as SourceFileType)}
          >
            <SelectTrigger
              aria-label={t('efs.imports.wizard.step1.title', { ns: 'imports' })}
              className="max-w-md"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sourceFileTypes
                .filter((typeName) => typeName !== 'other' && typeName !== 'evidence_file')
                .map((typeName) => (
                  <SelectItem key={typeName} value={typeName}>
                    {t(`efs.imports.source_types.${typeName}`, {
                      ns: 'imports',
                      defaultValue: typeName,
                    })}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </section>
      ) : null}

      {step === 2 ? (
        <section className="space-y-3 rounded-md border border-efs-border bg-efs-bg-panel p-4">
          <h2 className="text-sm font-semibold">
            {t('efs.imports.wizard.step2.title', { ns: 'imports' })}
          </h2>
          <SourceFileDropzone
            onFileSelected={handleSelectFile}
            selectedFile={pendingFile}
            isUploading={upload.isPending || attachFromLibrary.isPending}
            libraryAvailable={canUseFileLibrary}
            sourceMode={effectiveFileSourceMode}
            onSourceModeChange={(mode) => {
              setFileSourceMode(mode);
              setLibraryAttachError(null);
            }}
            selectedLibraryFile={selectedLibraryFile}
            onLibraryFileSelected={(file) => {
              setSelectedLibraryFile(file);
              setLibraryAttachError(null);
            }}
            batchId={batchId}
          />
          <LocalizedApiError
            error={libraryAttachError}
            showTechnicalDetails={hasPermission(user, 'system.admin')}
          />
          {uploadedFile ? (
            <Alert>
              <AlertTitle>{uploadedFile.original_file_name}</AlertTitle>
              <AlertDescription>
                <span className="font-mono text-xs">{uploadedFile.file_hash.slice(0, 24)}…</span>
              </AlertDescription>
            </Alert>
          ) : null}

          <div>
            <h3 className="mb-2 text-sm font-semibold">
              {t('efs.imports.source_files.title', { ns: 'imports' })}
            </h3>
            <SourceFileTable
              rows={sourceFilesQuery.data ?? []}
              isLoading={sourceFilesQuery.isLoading}
            />
          </div>
        </section>
      ) : null}

      {step === 3 ? (
        <section className="space-y-3 rounded-md border border-efs-border bg-efs-bg-panel p-4">
          <h2 className="text-sm font-semibold">
            {t('efs.imports.wizard.step3.title', { ns: 'imports' })}
          </h2>
          <ImportSheetsSummary summary={summary} />
          {!summary && uploadedFile ? (
            <Alert>
              <AlertDescription>
                {t('efs.imports.wizard.step3.description', { ns: 'imports' })}
              </AlertDescription>
            </Alert>
          ) : null}
        </section>
      ) : null}

      {step === 4 ? (
        <section className="space-y-3 rounded-md border border-efs-border bg-efs-bg-panel p-4">
          <h2 className="text-sm font-semibold">
            {t('efs.imports.wizard.step4.title', { ns: 'imports' })}
          </h2>
          <ImportSheetsSummary summary={summary} />
          <div>
            <h3 className="mb-2 text-sm font-semibold">
              {t('efs.imports.review.drawer.validation_messages', { ns: 'imports' })}
            </h3>
            {(() => {
              // Keep each staging row's sheet/row context beside its own
              // messages (spec-13 §8.5) instead of one anonymous flat list.
              const rowsWithMessages = (sampleRows.data?.rows ?? [])
                .filter((r) => r.validation_messages.length > 0)
                .slice(0, 10);
              if (rowsWithMessages.length === 0) {
                return <ValidationMessageList messages={[]} />;
              }
              return (
                <div className="space-y-2">
                  {rowsWithMessages.map((r) => (
                    <ValidationMessageList
                      key={r.staging_row_id}
                      messages={r.validation_messages}
                      sheet={r.source_sheet_name}
                      rowNumber={r.source_row_number}
                      onSelect={() => setSummaryRow(r)}
                    />
                  ))}
                </div>
              );
            })()}
          </div>
        </section>
      ) : null}

      {step === 5 ? (
        <section className="space-y-3 rounded-md border border-efs-border bg-efs-bg-panel p-4">
          <h2 className="text-sm font-semibold">
            {t('efs.imports.wizard.step5.title', { ns: 'imports' })}
          </h2>
          <ImportSheetsSummary summary={summary} />
          <div className="flex items-center gap-2">
            <PromoteAction job={job.data} jobId={importJobId ?? undefined} />
            <Button type="button" variant="outline" onClick={handleViewReview}>
              {t('efs.imports.wizard.actions.view_review', { ns: 'imports' })}
            </Button>
          </div>
        </section>
      ) : null}

      <div className="flex items-center justify-between border-t border-efs-border pt-3">
        <Button type="button" variant="outline" onClick={goBack} disabled={step === 1}>
          <ArrowLeft className="h-4 w-4" />
          {t('efs.imports.wizard.actions.back', { ns: 'imports' })}
        </Button>
        <span className="text-xs text-efs-text-muted">
          {t('efs.imports.wizard.step_indicator', {
            ns: 'imports',
            current: step,
            total: STEPS.length,
          })}
        </span>
        <Button
          type="button"
          onClick={goNext}
          disabled={
            !canAdvance || upload.isPending || attachFromLibrary.isPending || startJob.isPending
          }
        >
          {upload.isPending || attachFromLibrary.isPending || startJob.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : null}
          {step === 2
            ? t('efs.common.actions.upload')
            : step === 3
              ? t('efs.imports.wizard.actions.start_parse', { ns: 'imports' })
              : t('efs.imports.wizard.actions.next', { ns: 'imports' })}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
      <StagingRowDrawer
        open={!!summaryRow}
        onOpenChange={(open) => {
          if (!open) setSummaryRow(null);
        }}
        row={summaryRow}
        jobId={importJobId ?? undefined}
        focusOnOpen
      />
    </div>
  );
};

export default ImportWizardPage;
